import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Badge, BadgeType } from '@amzn/stencil-react-components/badge';
import { AuditStatus, type Audit } from '@amzn/gbsa-helios-typescript-client';
import { AUDIT_CARD_TEST_IDS } from '@amzn/gravion-ui-contracts';
import { AssigneesDisplay } from '../../../shared/ui/AssigneesDisplay';
import { TruncatedTitle } from '../../../shared/ui/TruncatedTitle';
import { DueDateDisplay } from '../../../shared/ui/DueDateDisplay';
import { CardChevron } from '../../../shared/ui/CardChevron';
import {
  ListCard,
  STATUS_BADGE_TYPE,
  STATUS_DISPLAY_LABEL,
} from '../../../shared/ui/ListCard';
import { getAuditUrgencyStatus, formatAuditDate, formatScorePercent } from '../logic';
import { useMessages } from '../../../shared/i18n/useMessages';

const CARD_BUNDLE = 'features.audit.translations.auditCard';
type CardKey = 'overdue' | 'dueDate' | 'score';

interface AuditCardProps {
  audit: Audit;
  onTap: (auditId: string) => void;
}

/**
 * Audit-specific list card content.
 * Renders title, location, status badge, assignee, and due date
 * inside the shared ListCard shell.
 */
export function AuditCard({ audit, onTap }: AuditCardProps): JSX.Element {
  const { t, f } = useMessages<CardKey>(CARD_BUNDLE);
  const { isUrgent, isOverdue } = getAuditUrgencyStatus(
    audit.expirationDate,
    audit.status,
  );

  const badgeType = STATUS_BADGE_TYPE[audit.status] ?? BadgeType.Neutral;
  const statusLabel = STATUS_DISPLAY_LABEL[audit.status] ?? audit.status;
  const dueDateLabel = formatAuditDate(audit.expirationDate);

  return (
    <ListCard
      status={audit.status}
      onTap={() => onTap(audit.auditId)}
      dataTestId={AUDIT_CARD_TEST_IDS.root(audit.auditId)}
    >
      <Row alignItems="center">
        <Col gridGap="dimensions.spacing.200" flex={1} minWidth={0}>
          <TruncatedTitle>{audit.name}</TruncatedTitle>

          <Text color="color.text.secondary">
            {audit.location}
            {audit.templateName ? ` · ${audit.templateName}` : ''}
          </Text>

          <Row gridGap="dimensions.spacing.200" alignItems="center">
            <Badge type={badgeType} value={statusLabel} />
            {audit.status === AuditStatus.COMPLETED &&
              audit.metrics?.scorePercent != null && (
                <Badge
                  type={BadgeType.Neutral}
                  value={f('score', {
                    percent: formatScorePercent(audit.metrics.scorePercent),
                  })}
                />
              )}
          </Row>

          <Row alignItems="center" gridGap="dimensions.spacing.300" flexWrap="wrap">
            <AssigneesDisplay assignees={audit.assignees} />
            <DueDateDisplay
              dueDateLabel={dueDateLabel}
              isOverdue={isOverdue}
              isUrgent={isUrgent}
              label={f('dueDate', { date: dueDateLabel })}
              overdueLabel={t('overdue')}
            />
          </Row>
        </Col>

        <CardChevron />
      </Row>
    </ListCard>
  );
}
